import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.6)',
  zIndex: 10000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const modal = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  padding: '28px',
  width: '740px',
  maxWidth: '96vw',
  maxHeight: '92vh',
  overflowY: 'auto',
  color: 'var(--text-primary)',
};

const btn = (bg, disabled = false) => ({
  padding: '8px 18px',
  background: disabled ? 'var(--bg-tertiary)' : bg,
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
  border: 'none',
  borderRadius: '6px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  marginRight: '8px',
});

const inputSt = {
  width: '100%',
  padding: '8px 12px',
  boxSizing: 'border-box',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  color: 'var(--text-primary)',
  fontSize: '13px',
};

const codeBlock = {
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  padding: '12px',
  fontFamily: 'monospace',
  fontSize: '11.5px',
  whiteSpace: 'pre',
  overflowX: 'auto',
  maxHeight: '280px',
  overflowY: 'auto',
};

const TYPE_DOT_COLORS = {
  text: '#4a9eff',
  number: '#a855f7',
  boolean: '#22c55e',
  select: '#f59e0b',
  multiselect: '#f97316',
  textarea: '#6b7280',
};

export default function N8nImportModal({ onClose, onImported }) {
  const [rawJson, setRawJson] = useState('');
  const [nodeIdOverride, setNodeIdOverride] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleImport = async () => {
    setError('');
    setResult(null);
    let descriptor;
    try {
      descriptor = JSON.parse(rawJson);
    } catch {
      setError('Invalid JSON — paste the full n8n node descriptor object.');
      return;
    }
    if (typeof descriptor !== 'object' || Array.isArray(descriptor)) {
      setError('Descriptor must be a JSON object, not an array.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/plugins/import-n8n-descriptor`,
        { descriptor, node_id_override: nodeIdOverride.trim() || null },
        { headers },
      );
      setResult(res.data);
      onImported?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Import failed. Check server logs for details.');
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(result.stub_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => {
    setResult(null);
    setRawJson('');
    setNodeIdOverride('');
    setError('');
  };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>🔌 Import n8n Node Descriptor</h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Generates a Python stub + field metadata from any n8n community node descriptor JSON
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {!result ? (
          /* ── Input form ── */
          <>
            <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '18px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              <strong style={{ color: 'var(--text-primary)' }}>How it works:</strong> Paste the JSON
              from an n8n node&apos;s <code>description</code> object (or the full file including it).
              xavvy reads the <code>properties[]</code> array to generate field definitions for the UI,
              then writes a Python <code>execute(context, params)</code> stub with one branch per operation.
              You fill in the real API calls, then reload and trust the plugin.
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                n8n Node Descriptor JSON <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <textarea
                value={rawJson}
                onChange={e => setRawJson(e.target.value)}
                spellCheck={false}
                placeholder={'{\n  "displayName": "Airtable",\n  "name": "airtable",\n  "description": "Read/write Airtable data",\n  "version": 2,\n  "credentials": [{ "name": "airtableApi", "required": true }],\n  "properties": [\n    { "displayName": "Operation", "name": "operation", "type": "options",\n      "options": [{ "name": "Read", "value": "read" }, { "name": "Create", "value": "create" }],\n      "default": "read" },\n    { "displayName": "Base ID", "name": "baseId", "type": "string", "default": "" }\n  ]\n}'}
                style={{ ...inputSt, fontFamily: 'monospace', fontSize: '12px', height: '220px', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Node ID Override{' '}
                <span style={{ color: 'var(--text-tertiary)' }}>(optional — auto-derived from name if blank)</span>
              </label>
              <input
                value={nodeIdOverride}
                onChange={e => setNodeIdOverride(e.target.value)}
                placeholder="e.g. n8n_airtable  (must be lowercase snake_case, no spaces)"
                style={inputSt}
              />
            </div>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', marginBottom: '14px', background: 'var(--error-color)22', color: 'var(--error-color)', border: '1px solid var(--error-color)', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btn('var(--bg-tertiary)')}>Cancel</button>
              <button
                onClick={handleImport}
                disabled={loading || !rawJson.trim()}
                style={btn('var(--accent-color)', loading || !rawJson.trim())}
              >
                {loading ? '⚙️ Generating…' : '⚙️ Generate & Install'}
              </button>
            </div>
          </>
        ) : (
          /* ── Result view ── */
          <>
            <div style={{ padding: '12px 16px', borderRadius: '6px', marginBottom: '20px', background: 'var(--success-color)22', color: 'var(--success-color)', border: '1px solid var(--success-color)', fontSize: '13px' }}>
              ✅ {result.message}
            </div>

            {/* Summary row */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' }}>
              {[
                { label: 'Node ID', value: result.node_id },
                { label: 'Display Name', value: result.display_name },
                { label: 'Version', value: result.version },
                { label: 'Fields', value: result.fields_count },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 14px', minWidth: '120px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, fontFamily: label === 'Node ID' ? 'monospace' : 'inherit' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Generated fields */}
            {result.fields.length > 0 && (
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Generated Fields ({result.fields_count})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {result.fields.map(f => (
                    <span key={f.name} title={f.description || f.name} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '3px 10px', borderRadius: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-color)',
                      fontSize: '12px', color: 'var(--text-primary)',
                    }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: TYPE_DOT_COLORS[f.type] || '#666', flexShrink: 0 }} />
                      {f.label}
                      <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>{f.type}</span>
                      {f.required && <span style={{ color: 'var(--error-color)', fontSize: '10px', fontWeight: 700 }}>*</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Stub code */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Generated Stub — <code style={{ fontWeight: 400 }}>custom_nodes/{result.node_id}/node.py</code>
                </div>
                <button onClick={copy} style={{ ...btn('var(--info-color)'), marginRight: 0, padding: '4px 12px', fontSize: '12px' }}>
                  {copied ? '✅ Copied' : '📋 Copy'}
                </button>
              </div>
              <div style={codeBlock}>{result.stub_code}</div>
            </div>

            {/* Next steps */}
            <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '18px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
              <strong style={{ color: 'var(--text-primary)' }}>Next steps:</strong>
              <ol style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                <li>Edit <code>custom_nodes/{result.node_id}/node.py</code> — replace each <code>raise NotImplementedError</code> with the real API call.</li>
                <li>In Plugin Governance, click <strong>Reload</strong> on <code>{result.node_id}</code> to pick up your edits.</li>
                <li>Review the code, then set trust to <strong>Trusted</strong> so it runs in-process (faster) instead of sandboxed.</li>
                <li>Add the node to your workflow from the sidebar — it appears under <em>Imported (n8n)</em>.</li>
              </ol>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={reset} style={btn('var(--bg-tertiary)')}>Import Another</button>
              <button onClick={onClose} style={btn('var(--accent-color)')}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
